import clsx from 'clsx'
import { createSerializer, useQueryStates } from 'nuqs'
import React, { ReactNode, useContext, useMemo } from 'react'

import { ampli } from '@Analytics/ampli'
import AngleLeftSVG from '@Assets/SVG/AngleLeft'
import AngleRightSVG from '@Assets/SVG/AngleRight'
import { ProductSearchStaticContext } from '@Globals/Algolia/Context'
import { createQueryStatesKeys, processURLSearchParams } from '@Globals/Algolia/Shared'
import { Trans } from '@Globals/Translations'

import styles from './pagination.module.scss'

const generatePageNumbers = (
  currentPageNumber: number,
  totalPages: number,
  maxPageNumbers: number,
): number[] => {
  if (totalPages <= maxPageNumbers) {
    // If total pages are less than or equal to maxPageNumbers, return all pages.
    return Array.from({ length: totalPages }, (_, i) => i + 1)
  }

  const halfMax = Math.floor(maxPageNumbers / 2)
  let startPage = Math.max(1, currentPageNumber - halfMax)
  let endPage = Math.min(totalPages, startPage + maxPageNumbers - 1)

  // Adjust if we're at the beginning or end of the list.
  if (endPage - startPage + 1 < maxPageNumbers) {
    endPage = Math.min(totalPages, startPage + maxPageNumbers - 1)
    startPage = Math.max(1, endPage - maxPageNumbers + 1)
  }

  const pageNumbers: number[] = []

  for (let i = startPage; i <= endPage; i += 1) {
    pageNumbers.push(i)
  }

  return pageNumbers
}

export const PaginationNumberList = ({ changePage, maxPageNumbers, page, pages }: {
  changePage: (page: number) => void
  maxPageNumbers: number
  page: number
  pages: number
}) => {
  const pageNumbers = generatePageNumbers(page, pages, maxPageNumbers)

  return (
    <div className={styles['number-list']}>
      <div>
        <button
          disabled={page === 1}
          onClick={(event) => {
            event.preventDefault()

            changePage(1)
          }}
          type="button"
        >
          ‹‹
        </button>
        {pageNumbers.map(pageNumber => (
          <button
            key={pageNumber}
            disabled={pageNumber === page}
            onClick={(event) => {
              event.preventDefault()

              changePage(pageNumber)
            }}
            type="button"
          >
            {pageNumber}
          </button>
        ))}
        <button
          disabled={page === pages}
          onClick={(event) => {
            event.preventDefault()

            changePage(pages)
          }}
          type="button"
        >
          ››
        </button>
      </div>
    </div>
  )
}

const PaginationButton = ({
  children,
  disabled = false,
  href,
  onClick,
}: {
  children: React.ReactNode
  disabled?: boolean
  href: string
  onClick: (event: React.MouseEvent<HTMLAnchorElement>) => void
}) => {
  if (disabled) {
    return (
      <span className={clsx(styles.button, styles.disabled)}>
        {children}
      </span>
    )
  }

  return (
    <a
      className={clsx(styles.button)}
      href={href}
      onClick={onClick}
    >
      {children}
    </a>
  )
}

export const Pagination = ({ children }: { children: ReactNode }) => (
  <div className={styles.pagination}>
    {children}
  </div>
)

export const PaginationArrows = ({
  onClick,
  pages,
}: {
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void
  pages: number
}) => {
  const { facetConfig } = useContext(ProductSearchStaticContext)
  const queryStatesKeysMap = useMemo(() => createQueryStatesKeys(facetConfig), [facetConfig])
  const [queryStates, setQueryStates] = useQueryStates(queryStatesKeysMap)
  const serializer = useMemo(() => createSerializer(queryStatesKeysMap, { processUrlSearchParams: processURLSearchParams }), [queryStatesKeysMap])

  if (queryStates.page < 1 || queryStates.page > pages) {
    return null
  }

  return (
    <div className={styles.arrows}>
      <PaginationButton
        disabled={queryStates.page <= 1}
        href={serializer({ ...queryStates, page: queryStates.page - 1 })}
        onClick={(e) => {
          e.preventDefault()
          setQueryStates((prev) => {
            const page = Math.max(1, prev.page - 1)
            if (prev?.page === page) {
              return prev
            }

            ampli.plApplyPage({ page, source_id: 'pagination_previous' })
            return { ...prev, page }
          })
          onClick?.(e)
        }}
      >
        <AngleLeftSVG />
      </PaginationButton>

      <span className={styles.text}>
        <Trans
          i18nKey="common:PAGINATION_PAGE_OF_PAGES"
          values={{ page: queryStates.page, pages }}
        />
      </span>

      <PaginationButton
        disabled={queryStates.page >= pages}
        href={serializer({ ...queryStates, page: queryStates.page + 1 })}
        onClick={(e) => {
          e.preventDefault()
          setQueryStates((prev) => {
            const page = Math.min(pages, prev.page + 1)
            if (prev?.page === page) {
              return prev
            }

            ampli.plApplyPage({ page, source_id: 'pagination_next' })
            return { ...prev, page }
          })
          onClick?.(e)
        }}
      >
        <AngleRightSVG />
      </PaginationButton>
    </div>
  )
}

export const PaginationShowingText = ({
  currentHits,
  defaults,
  i18nKey = 'common:PAGINATION_SHOWING_FROM_TO_OF_TOTAL_PRODUCTS',
  page,
  perPage = 60,
}: {
  currentHits: number
  defaults?: string
  i18nKey?: string
  page: number
  perPage?: number
}) => (
  <p className={styles.showing}>
    <Trans
      defaults={defaults}
      i18nKey={i18nKey}
      tOptions={{
        from: Math.max(((page || 1) - 1) * perPage + 1, 0),
        to: Math.min((page || 1) * perPage, currentHits),
        total: currentHits,
      }}
    />
  </p>
)
